import type { Person } from 'shared/interfaces/people.interface';

import { Button } from 'components/buttons/button/button';
import { InputField } from 'components/fields/input-field/input-field';
import { GrayPanel } from 'components/panels/gray-panel/gray-panel';
import { Container } from 'components/template/container/container';
import { FormHeader } from 'components/template/form-header/FormHeader';
import { ConfirmationModal } from 'components/template/modals/confirmation-modal';
import { PermissionsContext } from 'contexts';
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { MODAL_PEOPLE } from 'shared/constants/modals';
import { validateField } from 'shared/utils';
import CheckIfFormIsDirty from 'shared/utils/checkIfFormIsDirty';
import { useAppDispatch, useAppSelector } from 'store';
import { closeModal } from 'store/modals/modals.slice';
import { removePeopleFromStore } from 'store/people/people.slice';
import {
  createPeopleThunk,
  removePeopleThunk,
  updatePeopleThunk,
} from 'store/people/people.thunks';
import { vocabulary } from 'vocabulary/german';

export const PeopleForm = () => {
  const dispatch = useAppDispatch();
  const { permissions } = useContext(PermissionsContext);
  const people = useAppSelector((state) => state.people);
  const currentYearFrozen = useAppSelector(
    (state) => state.years.currentYearFrozen,
  );

  const initialValues: Person = {
    id: people.current?.id ?? null,
    name: people.current?.name ?? '',
  };

  const handleSave = (id: number | null, person: Person) => {
    if (id) {
      // when we UPDATE the People behavior
      dispatch(updatePeopleThunk({ name: person.name, id }));
    } else {
      // when we CREATE the People behavior
      dispatch(createPeopleThunk({ name: person.name }));
      dispatch(closeModal({ name: MODAL_PEOPLE }));
    }
  };

  const renderSaveButton = () => {
    if (permissions.edit === true && !currentYearFrozen) {
      return <Button type={'submit'} text={vocabulary.buttons.save} />;
    }
  };

  const renderDeleteButton = (id: number | null) => {
    if (!id) {
      return null;
    }

    if (permissions.remove === true && !currentYearFrozen) {
      return (
        <Button
          className={'delete-button'}
          text={vocabulary.buttons.delete}
          onClick={() => showConfirmationModal(id)}
        />
      );
    }
  };

  const showConfirmationModal = (id: number | null) => {
    if (id) {
      confirmAlert({
        customUI: ({ onClose }) => {
          return (
            <ConfirmationModal
              onClose={onClose}
              onOk={() => handleDelete(id)}
            />
          );
        },
      });
    }
  };

  const handleDelete = (id: number) => {
    dispatch(removePeopleThunk(id));
    dispatch(removePeopleFromStore());
    dispatch(closeModal({ name: MODAL_PEOPLE }));
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(values: Person) => {
        handleSave(initialValues.id, values);
      }}
    >
      {({ errors, touched, dirty }) => (
        <Form>
          <CheckIfFormIsDirty dirty={dirty} />
          <FormHeader
            renderSave={renderSaveButton()}
            renderDelete={renderDeleteButton(initialValues.id)}
          />
          <GrayPanel text={vocabulary.forms.people.description} people />

          <Container>
            <div className="field-wrapper">
              <InputField
                name="name"
                placeholder={vocabulary.forms.people.placeholders.name}
                validate={validateField}
                className={'form__field_big'}
              />
              {errors.name && touched.name && (
                <p className={'field-error'}>{errors.name}</p>
              )}
            </div>
          </Container>
        </Form>
      )}
    </Formik>
  );
};
